import type { SwitchboardFlowData } from '../converters/shared/reactFlowTypes'
import type { SwitchboardTranslateFn } from '../types'
import {
  formatNodeNameForError,
  getEndpointLabelFromHandle,
} from '../utils/connectionValidation'
import type { FixedHandleRouteConflict } from '../utils/fixedHandleRouteConflicts'
import { text } from '../utils/text'

type HandleLabel = {
  key: string
  fallback: string
}

const HANDLE_LABELS: Record<string, HandleLabel> = {
  'schedule.whenOpen': {
    key: 'switchboard.nodes.schedule.open',
    fallback: 'Open',
  },
  'schedule.whenClosed': {
    key: 'switchboard.nodes.schedule.closed',
    fallback: 'Closed',
  },
  'schedule.holiday': {
    key: 'switchboard.nodes.schedule.holiday',
    fallback: 'Holiday',
  },
  'day_night.whenOpen': {
    key: 'switchboard.nodes.dayNight.whenOpen',
    fallback: 'When Open',
  },
  'day_night.whenClosed': {
    key: 'switchboard.nodes.dayNight.whenClosed',
    fallback: 'When Closed',
  },
  'queue.onTimeout': {
    key: 'switchboard.nodes.transfer.onTimeout',
    fallback: 'On Timeout',
  },
  'queue.onEscape': {
    key: 'switchboard.nodes.queue.onEscape',
    fallback: 'On Escape',
  },
  'transfer_rollover.noAnswer': {
    key: 'switchboard.nodes.transferAndRollover.noAnswer',
    fallback: 'No Answer',
  },
  'transfer_rollover.busy': {
    key: 'switchboard.nodes.transferAndRollover.busy',
    fallback: 'Busy',
  },
  'auto_attendant.onTimeout': {
    key: 'switchboard.nodes.transfer.onTimeout',
    fallback: 'On Timeout',
  },
  'auto_attendant.onInvalid': {
    key: 'switchboard.nodes.transfer.onInvalid',
    fallback: 'On Invalid',
  },
  'interactive_webhook.onTimeout': {
    key: 'switchboard.nodes.transfer.onTimeout',
    fallback: 'On Timeout',
  },
  'interactive_webhook.onInvalid': {
    key: 'switchboard.nodes.transfer.onInvalid',
    fallback: 'On Invalid',
  },
  'ai_agent_v1.onError': {
    key: 'switchboard.nodes.aiAgent.onError',
    fallback: 'On Error',
  },
}

export function getFixedHandleRouteConflictDescription({
  conflict,
  flowData,
  schedules,
  t,
}: {
  conflict: FixedHandleRouteConflict
  flowData: SwitchboardFlowData
  schedules: Array<{ id: string; name: string }>
  t?: SwitchboardTranslateFn
}): string {
  const sourceNode = flowData.nodes.find(
    (node) => node.id === conflict.sourceNodeId,
  )
  const defaultNodeName = formatNodeNameForError(conflict.sourceNodeType)
  let nodeName = text(
    t,
    `switchboard.nodeLabels.${conflict.sourceNodeType}`,
    defaultNodeName,
  )

  if (sourceNode?.type === 'schedule') {
    const scheduleId = sourceNode.data.scheduleId
    const schedule = schedules.find(
      (candidate) => candidate.id === String(scheduleId),
    )
    nodeName = schedule?.name || nodeName
  }

  const handleLabel = HANDLE_LABELS[
    `${conflict.sourceKind}.${conflict.sourceHandle}`
  ] ?? {
    key: `switchboard.fixedHandles.${conflict.sourceHandle}`,
    fallback:
      getEndpointLabelFromHandle(
        conflict.sourceNodeType,
        conflict.sourceHandle,
      ) || conflict.sourceHandle,
  }
  const routeName = text(t, handleLabel.key, handleLabel.fallback)

  return text(
    t,
    'switchboard.edit.routeConflict.description',
    '{{node}} → {{route}} has multiple destinations. Remove one before saving.',
    { node: nodeName, route: routeName },
  )
}
